import { useEffect, useRef, useState } from "react";
import { HeroContent } from "./HeroContent";
import { LoopVideo } from "./LoopVideo";
import { FEATURED_SONG, LOGO } from "@/data/site";
import { UNDERWATER_DIM } from "./underwater";
import { StardustDissolve } from "./StardustDissolve";
import { useSiteAudio } from "./SiteAudio";

// Wordmark + "Tap to Enter" clear quickly once the tap lands...
const TEXT_FADE_MS = 300;
// ...while she breaks into stardust and her photo layer fades under it.
const PORTAL_FADE = "opacity 1.5s cubic-bezier(0.4,0,0.8,0.6) 0.2s";
// The dive starts moving once she's mostly gone (muted video, so a delayed
// play() is still allowed outside the tap gesture).
const DIVE_DELAY_MS = 450;
// The dive crossfades into the Landing loop over its last 1.5s: the Landing
// loop restarts on its first frame (the frame the dive ends on) and the two
// blend while both are moving, then the hero content fades up.
const HERO_FADE_MS = 1500;
const CROSSFADE_LEAD_S = HERO_FADE_MS / 1000;
// Safety nets if 'ended' never fires: from the tap (video never starts, e.g.
// no data yet on a slow connection) and from 'playing' (a stall mid-dive).
const START_FALLBACK_MS = 12000;
const PLAY_FALLBACK_MS = 8000;

// Portal + Landing loops (mobile crops) — see CinematicExperience.jsx for
// what each one is. The Portal loop is re-encoded at half speed.
const PORTAL_LOOP = "/jennrock-portal-loop-mobile-v2.mp4";
const PORTAL_POSTER = "/jennrock-portal-loop-mobile-v2.webp";
const LANDING_LOOP = "/jennrock-landing-loop-mobile-v2.mp4";
const LANDING_POSTER = "/jennrock-landing-loop-mobile-v2.webp";

// Transition — Trevor's dedicated 9:16 Seedance dive (6s), rendered from the
// mobile clean start frame. Seedance drifted it ~1.5% zoom / a few px off
// both the Portal (start) and Landing (end) — the same offset at both ends —
// so the correction is baked into the encode and it lines up on any screen.
const TRANSITION = "/jennrock-dive-mobile-v2.mp4";
const TRANSITION_POSTER = "/jennrock-dive-mobile-v2.webp";
// Her silhouette (Portal art vs the clean start frame) — where the dust spawns.
const HER_MASK = "/jennrock-her-silhouette-mobile.png";
// The Portal still cut to HER_MASK as a transparent WebP (no CSS mask, so
// nothing can paint unmasked while she fades). Its edge is feathered ~22px
// outward: the silhouette takes in some of the Portal's glow around her head,
// which showed as a hard-edged aura once the tap cut to the dive's first frame.
const HER_CUTOUT = "/jennrock-her-cutout-mobile-v2.webp";

// All three full-bleed layers share one framing so the seams stay exact.
const OBJECT_POSITION = "50% 70%";
const DUST_POSITION = [0.5, 0.7];

// Mobile-only Portal -> Transition -> Hero sequence. Tap-to-enter replaces
// the desktop's scroll-scrub entirely — a fixed 100svh viewport, three
// discrete phases driven by a single tap. As in the sierra-marin-website
// template, the transition video's own 'ended' event drives the hero seam.
export const MobilePortal = () => {
  const [phase, setPhase] = useState("portal"); // portal | transitioning | hero
  const [entered, setEntered] = useState(false);
  // True once the dive video is actually rendering frames. Until then its
  // first frame is shown as a plain <img> (iOS Safari doesn't apply
  // object-fit/object-position to a <video> poster, so the poster would sit
  // at the wrong size), and a slow download just holds on the clean beach.
  const [diving, setDiving] = useState(false);
  const enteringRef = useRef(false);
  const timerRef = useRef(null);
  const playTimerRef = useRef(null);
  const transitionRef = useRef(null);
  const landingRef = useRef(null);
  const audioRef = useSiteAudio();

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleTap = () => {
    if (enteringRef.current || phase !== "portal") return;
    enteringRef.current = true;
    // In-gesture .play() for autoplay-with-sound policies (no-op until a
    // rights-cleared hook clip is supplied — FEATURED_SONG.audioSrc).
    audioRef.current?.play().catch(() => {});
    let handedOff = false;
    const toHero = () => {
      if (handedOff) return;
      handedOff = true;
      clearTimeout(timerRef.current);
      const l = landingRef.current;
      if (l) {
        l.currentTime = 0;
        l.play().catch(() => {});
      }
      setPhase("hero");
    };
    const v = transitionRef.current;
    setPhase("transitioning");
    if (!v) {
      timerRef.current = setTimeout(toHero, 1200);
      return;
    }
    v.onended = toHero;
    v.ontimeupdate = () => {
      if (v.duration && v.currentTime >= v.duration - CROSSFADE_LEAD_S) {
        v.ontimeupdate = null;
        toHero();
      }
    };
    v.onplaying = () => {
      setDiving(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(toHero, PLAY_FALLBACK_MS);
    };
    // Kick the download now (iOS often ignores preload), start the dive once
    // she's mostly dissolved. If the browser refuses to play, go to the hero.
    v.load();
    playTimerRef.current = setTimeout(() => v.play().catch(toHero), DIVE_DELAY_MS);
    timerRef.current = setTimeout(toHero, START_FALLBACK_MS);
  };

  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      clearTimeout(playTimerRef.current);
    },
    []
  );

  const portalOn = phase === "portal";
  const heroOn = phase === "hero";
  const pe = (on) => (on ? "auto" : "none");

  return (
    <section data-testid="mobile-portal" className="relative w-full overflow-hidden bg-[var(--sm-black)]" style={{ height: "100svh" }}>
      {/* Layer 0: Landing loop — underwater, looking up at the galaxy. */}
      {/* Snaps on under the still-opaque dive; only the dive fades, so the
          blend never dips darker in the middle. */}
      <div className="absolute inset-0" style={{ opacity: heroOn ? 1 : 0 }}>
        <LoopVideo
          src={LANDING_LOOP}
          poster={LANDING_POSTER}
          testId="landing-loop"
          videoRef={landingRef}
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: OBJECT_POSITION }}
        />
      </div>

      {/* Transition — its clean first frame sits under the Portal as an
          <img>; on tap the dive plays once over it, then crossfades into the
          Landing loop on 'ended'. */}
      <div className="absolute inset-0" style={{ opacity: heroOn ? 0 : 1, transition: `opacity ${HERO_FADE_MS}ms ease` }}>
        <img
          src={TRANSITION_POSTER}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
          style={{ objectPosition: OBJECT_POSITION }}
        />
        <video
          ref={transitionRef}
          data-testid="transition-video"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ opacity: diving ? 1 : 0, objectPosition: OBJECT_POSITION }}
          src={TRANSITION}
          muted
          playsInline
          preload="auto"
          tabIndex={-1}
          aria-hidden
        />
      </div>

      {/* Underwater dim (see UNDERWATER_DIM) — eases in as the dive breaks
          the surface (~4s into the 6s clip). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-black"
        style={{
          opacity: diving || heroOn ? UNDERWATER_DIM : 0,
          transition: diving ? "opacity 2s ease 3.8s" : "none",
        }}
      />

      {/* Layer 1: Portal — her key-art loop + wordmark; the whole layer is
          the tap target. On tap she dissolves into stardust. */}
      <button
        type="button"
        onClick={handleTap}
        data-testid="mobile-portal-tap-target"
        aria-label="Tap to enter"
        className="absolute inset-0 z-10 h-full w-full cursor-pointer overflow-hidden"
        style={{
          opacity: entered ? 1 : 0,
          pointerEvents: pe(portalOn && entered),
          transition: "opacity 800ms ease-out",
        }}
      >
        {/* On tap the background cuts instantly to the dive's matching first
            frame (below); only her silhouette (next layer) fades. */}
        <div className="absolute inset-0" style={{ opacity: portalOn ? 1 : 0, willChange: "opacity" }}>
          <LoopVideo
            src={PORTAL_LOOP}
            poster={PORTAL_POSTER}
            alt="Jenn Rock on a beach under the Milky Way"
            testId="portal-loop"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ objectPosition: OBJECT_POSITION }}
          />
        </div>
        {/* Her, alone: the pre-cut still, on screen from page load. */}
        <img
          src={HER_CUTOUT}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          style={{
            objectPosition: OBJECT_POSITION,
            opacity: portalOn ? 1 : 0,
            willChange: "opacity",
            transition: portalOn ? "none" : PORTAL_FADE,
          }}
        />
        <StardustDissolve active={!portalOn} mask={HER_MASK} poster={PORTAL_POSTER} objectPosition={DUST_POSITION} count={275} />
        <div className="absolute inset-0" style={{ opacity: portalOn ? 1 : 0, transition: `opacity ${TEXT_FADE_MS}ms ease-out` }}>
        <img
          src={LOGO.wordmark}
          alt="Jenn Rock"
          className="absolute left-1/2 top-[13%] w-[74vw] max-w-[19rem] -translate-x-1/2"
          style={{ filter: "drop-shadow(0 0 16px rgba(255,46,151,0.35)) drop-shadow(0 2px 14px rgba(0,0,0,0.8))" }}
        />
        {/* Sits in the open sky between the wordmark and her head.
            Centered by a full-width flex row, not translate — the breathe
            animation writes `transform`, which would clobber a
            -translate-x-1/2 and push the label off-center. */}
        <span className="absolute inset-x-0 top-[32%] flex justify-center">
          <span
            className="font-accent whitespace-nowrap text-sm uppercase tracking-[0.3em] text-[var(--sm-gold-light)] text-legible"
            style={{ animation: "breathe 2.4s ease-in-out infinite" }}
          >
            Tap to Enter
          </span>
        </span>
        </div>
      </button>

      {/* Hero content — logo/tagline/bio/CTA, mobile variant */}
      <div className="absolute inset-x-0 bottom-0 top-0 z-20" style={{ opacity: heroOn ? 1 : 0, pointerEvents: pe(heroOn), transition: "opacity 1.2s ease 0.6s" }}>
        <HeroContent active={phase === "hero"} mobile />
      </div>


      {/* Persistent vignette so header/footer areas never show a hard line */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-30"
        style={{
          background:
            "linear-gradient(to bottom, #000 0%, transparent 14%, transparent 86%, #000 100%), linear-gradient(to right, rgba(0,0,0,0.35) 0%, transparent 10%, transparent 90%, rgba(0,0,0,0.35) 100%)",
        }}
      />
    </section>
  );
};
